<template>
  <SettingsCard
    :title="$t('settings.company.additional.title')"
    icon="mdi-wrench-outline"
    :model-value="isOpen"
    @update:modelValue="setOpen"
  >
    <div class="px-4 pt-4">
      <Feature
        v-slot="{ feature }"
        :feature-slug="featureNames.COMPANY"
      >
        <v-row v-if="feature.config.isSloganEditable">
          <v-col
            cols="12"
            sm="6"
            class="px-0 px-md-2"
          >
            <div>
              <div class="d-flex align-center">
                <span class="font-weight-bold">
                  {{ $t('settings.company.additional.body-texts.0') }}
                </span>
              </div>
              <span class="text-grey">
                {{ $t('settings.company.additional.body-texts.1') }}<br />
                {{ $t('settings.company.additional.body-texts.2') }}
              </span>
            </div>
          </v-col>
          <v-col
            cols="12"
            sm="6"
            class="justify-center px-0 mt-4 d-flex px-md-2"
          >
            <TextSettings
              name="slogan"
              :disabled="showUpselling"
              :label="$t('settings.company.additional.slogan-field.label')"
              :placeholder="$t('settings.company.additional.slogan-field.placeholder')"
              :value="company.slogan"
              @update="update"
            />
          </v-col>
        </v-row>
      </Feature>

      <Feature
        v-slot="{ feature }"
        :feature-slug="featureNames.COMPANY"
      >
        <v-row v-if="feature.config.isCommissionEditable">
          <v-col
            cols="12"
            sm="6"
            class="px-0 px-md-2"
          >
            <div>
              <div class="d-flex align-center">
                <span class="font-weight-bold">
                  {{ $t('settings.company.additional.body-texts.3') }}
                </span>
              </div>
              <span class="text-grey">
                {{ $t('settings.company.additional.body-texts.4') }}
              </span>
            </div>
          </v-col>
          <v-col
            cols="12"
            sm="6"
            class="justify-center px-0 mt-4 d-flex px-md-2"
          >
            <TextSettings
              suffix="%"
              type="number"
              name="commission"
              :disabled="showUpselling"
              :label="$t('settings.company.additional.commission-field.label')"
              :placeholder="$t('settings.company.additional.commission-field.placeholder')"
              :value="company.commission"
              @update="update"
            />
          </v-col>
        </v-row>
      </Feature>

      <Feature
        v-slot="{ feature }"
        :feature-slug="featureNames.COMPANY"
      >
        <v-row v-if="feature.config.isRegionEditable">
          <v-col
            cols="12"
            sm="6"
            class="px-0 px-md-2"
          >
            <div>
              <div class="d-flex align-center">
                <span class="font-weight-bold">
                  {{ $t('settings.company.additional.region.title') }}
                </span>
              </div>
              <span class="text-grey">
                {{ $t('settings.company.additional.region.subtitle') }}
              </span>
            </div>
          </v-col>
          <v-col
            cols="12"
            sm="6"
            class="justify-center px-0 mt-4 d-flex px-md-2"
          >
            <TextSettings
              type="text"
              name="regionName"
              :disabled="showUpselling"
              :label="$t('settings.company.additional.region-field.label')"
              :placeholder="$t('settings.company.additional.region-field.placeholder')"
              :value="company.regionName"
              @update="update"
            />
          </v-col>
        </v-row>
      </Feature>
    </div>
    <BlurredBackdropUpselling v-if="showUpselling" />
  </SettingsCard>
</template>

<script>
import BlurredBackdropUpselling from '@/components/BlurredBackdropUpselling.vue'
import SettingsCard from '@/components/SettingsCard.vue'
import { useQueryOpen } from '@/composables/useQueryOpen'
import bus, { eventNames } from '@/lib/eventBus'
import feature from '@/mixins/feature'
import PENDING_CHANGES_FOR_REGENERATION from '@/queries/PendingChangesForRegeneration.gql'

import UPDATE_COMPANY_COMMISSION from './queries/UpdateCompanyCommission.gql'
import UPDATE_COMPANY_REGION from './queries/UpdateCompanyRegion.gql'
import UPDATE_COMPANY_SLOGAN from './queries/UpdateCompanySlogan.gql'
import TextSettings from './TextSettings'

export default {
  setup() {
    const { isOpen, setOpen } = useQueryOpen({ settingName: 'other' })
    return {
      isOpen,
      setOpen,
    }
  },
  components: {
    SettingsCard,
    TextSettings,
    BlurredBackdropUpselling,
  },
  mixins: [feature],
  props: {
    company: {
      type: Object,
      default: () => ({}),
    },
    showUpselling: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      slugError: null,
      slugSuccess: null,
    }
  },
  methods: {
    async update({ name, value }) {
      const mutations = {
        slogan: UPDATE_COMPANY_SLOGAN,
        commission: UPDATE_COMPANY_COMMISSION,
        regionName: UPDATE_COMPANY_REGION,
      }

      if (!mutations[name]) {
        bus.$emit(eventNames.SHOW_SNACKBAR, {
          color: 'error',
          text: this.$t('alerts.settings.company.error'),
        })
        return
      }

      try {
        await this.$apollo.mutate({
          mutation: mutations[name],
          variables: {
            input: {
              companyId: this.$auth.user.companyId,
              [name]: value,
            },
          },
          refetchQueries: [
            {
              query: PENDING_CHANGES_FOR_REGENERATION,
              variables: { companyId: this.$auth.user.companyId },
            },
          ],
        })
        bus.$emit(eventNames.SHOW_SNACKBAR, {
          color: 'success',
          text: this.$t('alerts.settings.company.success'),
        })
      } catch (e) {
        bus.$emit(eventNames.SHOW_SNACKBAR, {
          color: 'error',
          text: this.$t('alerts.settings.company.error'),
        })
      }
    },
  },
}
</script>
